import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { FieldValue, withoutEmpty } from "./customFields";
import { maskMeterNumber, MeterType } from "./vendEvents";

/**
 * Energy Management events for the web app, mirroring the "Energy Management" category of
 * the shared analytics event sheet.
 *
 * The flow and its steps are seeded by the backend migration SeedProductAnalyticsCatalog
 * (flow 11) and must stay in step with it — ingestion accepts unknown codes over HTTP and
 * then silently discards them — so the codes live here and nowhere else. The first three
 * also feed the backend's energy-chart-health report, which reads `has_data` by name.
 */
export const ENERGY_MANAGEMENT_FLOW = "energy_management";

export const ENERGY_MANAGEMENT_EVENTS = {
	opened: "energy_mgmt_opened",
	analyticsViewed: "energy_analytics_viewed",
	analyticsDataRendered: "energy_analytics_data_rendered",
	lowThresholdReminderToggled: "low_threshold_reminder_toggled",
	autoRechargeToggled: "auto_recharge_toggled",
	billViewed: "bill_viewed",
	energyProfileSet: "energy_profile_set",
} as const;

export type EnergyProfileStatus = "success" | "failed";

const track = (event: string, fields: Record<string, FieldValue>): void => {
	BusinessAnalytics.track(event, ENERGY_MANAGEMENT_FLOW, withoutEmpty(fields));
};

/** `meterNumber` is the raw meter or account number; only its masked form is sent. */
export const trackEnergyManagementOpened = (meterNumber: string): void => {
	track(ENERGY_MANAGEMENT_EVENTS.opened, { meter_id_masked: maskMeterNumber(meterNumber) });
};

/** `dateRange` is the selected period code, e.g. "OneMonth" — not the computed dates. */
export const trackEnergyAnalyticsViewed = (dateRange: string): void => {
	track(ENERGY_MANAGEMENT_EVENTS.analyticsViewed, { date_range: dateRange });
};

/** A failed load counts as no data, the same as the chart shows it. */
export const trackEnergyAnalyticsDataRendered = (hasData: boolean): void => {
	track(ENERGY_MANAGEMENT_EVENTS.analyticsDataRendered, { has_data: hasData });
};

export const trackLowThresholdReminderEnabled = (thresholdValue: number | null | undefined): void => {
	track(ENERGY_MANAGEMENT_EVENTS.lowThresholdReminderToggled, { state: "enabled", threshold_value: thresholdValue });
};

export const trackLowThresholdReminderDisabled = (): void => {
	track(ENERGY_MANAGEMENT_EVENTS.lowThresholdReminderToggled, { state: "disabled" });
};

export const trackAutoRechargeEnabled = (amount: number | null | undefined): void => {
	track(ENERGY_MANAGEMENT_EVENTS.autoRechargeToggled, { state: "enabled", amount });
};

export const trackAutoRechargeDisabled = (): void => {
	track(ENERGY_MANAGEMENT_EVENTS.autoRechargeToggled, { state: "disabled" });
};

/** `billId` is left out when the bill has no id to report. */
export const trackBillViewed = (meterType: MeterType, billId?: string | null): void => {
	track(ENERGY_MANAGEMENT_EVENTS.billViewed, { bill_id: billId, meter_type: meterType });
};

export const trackEnergyProfileSet = (status: EnergyProfileStatus): void => {
	track(ENERGY_MANAGEMENT_EVENTS.energyProfileSet, { status });
};
